if (!customElements.get('rhen-custom-builder')) {
  customElements.define('rhen-custom-builder', class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.steps = [...this.querySelectorAll('[data-step]')];
      this.current = 1;
      this.endpoint = this.dataset.aiEndpoint || '';
      this.currency = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });
      this.querySelectorAll('[data-next]').forEach((button) => button.addEventListener('click', () => this.next()));
      this.orderButton = this.querySelector('[data-next-order]');
      this.querySelectorAll('[data-back]').forEach((button) => button.addEventListener('click', () => this.show(this.current - 1)));
      this.form.addEventListener('input', () => this.refresh());
      this.form.addEventListener('change', () => this.refresh());
      this.querySelector('[data-generate]').addEventListener('click', () => this.generate());
      this.form.addEventListener('submit', () => this.buildSummary());
      this.refresh();
    }

    value(name) {
      const selected = this.form.querySelector(`[name="${name}"]:checked`) || this.form.elements[name];
      return selected ? selected.value : '';
    }

    label(name) {
      const field = this.form.elements[name];
      if (field && field.tagName === 'SELECT') return field.options[field.selectedIndex]?.text || field.value;
      return this.value(name);
    }

    next() {
      const active = this.panels.find((panel) => Number(panel.dataset.panel) === this.current);
      const required = [...active.querySelectorAll('[required]')];
      if (!required.every((field) => field.reportValidity())) return;
      if (this.current === 2) this.buildSummary();
      this.show(Math.min(3, this.current + 1));
    }

    show(number) {
      this.current = number;
      this.panels.forEach((panel) => panel.classList.toggle('is-active', Number(panel.dataset.panel) === number));
      this.steps.forEach((step) => {
        const n = Number(step.dataset.step);
        step.classList.toggle('is-active', n === number);
        step.classList.toggle('is-complete', n < number);
      });
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    refresh() {
      this.syncDimensionsWithShape();
      this.syncFinishWithMaterial();
      this.syncBackerRequirement();
      const sign = this.querySelector('[data-sign]');
      const copy = (this.value('contact[Sign text]') || 'YOUR SIGN').trim();
      const finish = this.value('contact[Finish]') || 'black';
      const shape = this.value('contact[Shape]') || 'rectangle';
      sign.dataset.finish = finish;
      sign.dataset.shape = shape;
      this.querySelector('[data-sign-copy]').textContent = copy;
      this.querySelector('[data-sign-motif]').textContent = 'CUSTOM METALWORK';
      this.updateEstimate();
      const previewIsCurrent = this.generatedSignature === this.designSignature();
      if (this.orderButton) {
        this.orderButton.disabled = !previewIsCurrent;
        this.orderButton.hidden = !previewIsCurrent;
      }
      const generateButton = this.querySelector('[data-generate]');
      if (generateButton && !generateButton.disabled) {
        generateButton.textContent = previewIsCurrent ? 'Regenerate preview' : 'See my concept preview';
        generateButton.classList.toggle('rhen-builder__button--secondary', previewIsCurrent);
        generateButton.classList.toggle('rhen-builder__button--preview', !previewIsCurrent);
      }
    }

    designSignature() {
      return JSON.stringify([
        this.value('contact[Width]'), this.value('contact[Height]'), this.value('contact[Shape]'),
        this.value('contact[Material]'), this.value('contact[Finish]'), this.value('contact[Backer colour]'),
        this.value('contact[Construction]'), this.value('contact[Use]'), this.value('contact[Sign text]'),
        this.value('contact[Design description]')
      ]);
    }

    syncDimensionsWithShape() {
      const shape = this.value('contact[Shape]');
      const isRound = shape === 'round';
      const isSquare = shape === 'square';
      const isSingleDimension = isRound || isSquare;
      const width = this.form.elements['contact[Width]'];
      const height = this.form.elements['contact[Height]'];
      const sizeGuidance = this.querySelector('[data-size-guidance]');
      this.querySelector('[data-width-label]').textContent = isRound ? 'Diameter (inches)' : isSquare ? 'Side length (inches)' : 'Width (inches)';
      this.querySelector('[data-height-field]').hidden = isSingleDimension;
      width.min = isRound ? '14' : isSquare ? '12' : '8';
      height.min = '8';
      if (isSingleDimension) {
        height.value = width.value;
        const minimum = isRound ? 14 : 12;
        const validationMessage = isRound
          ? 'Round signs must be at least 14 inches in diameter.'
          : 'Square signs must be at least 12 inches per side.';
        width.setCustomValidity(Number(width.value) < minimum ? validationMessage : '');
        sizeGuidance.textContent = isRound ? 'Minimum 14″ diameter.' : 'Minimum 12″ per side.';
      } else {
        const widthValue = Number(width.value);
        const heightValue = Number(height.value);
        const tooSmallOverall = widthValue < 12 && heightValue < 12;
        width.setCustomValidity(tooSmallOverall ? 'At least one side must be 12 inches or larger.' : '');
        sizeGuidance.textContent = 'Minimum 8″ per side, with at least one side 12″ or larger.';
      }

      const detailNote = this.querySelector('[data-detail-guidance]');
      if (detailNote) detailNote.textContent = `${this.detailTierLabel()} detail is automatically applied for clean plasma cutting at this size.`;
    }

    detailTier() {
      const width = Number(this.value('contact[Width]')) || 0;
      const height = Number(this.value('contact[Height]')) || 0;
      const shape = this.value('contact[Shape]');
      const controllingDimension = shape === 'round' || shape === 'square' ? width : Math.min(width, height);
      if (controllingDimension < 18) return 'simple';
      if (controllingDimension < 30) return 'moderate';
      return 'moderately detailed';
    }

    detailTierLabel() {
      return ({ simple: 'Simple', moderate: 'Moderate', 'moderately detailed': 'Moderately detailed' })[this.detailTier()];
    }

    detailGuidance() {
      const tier = this.detailTier();
      if (tier === 'simple') return 'Simple detail only: bold silhouettes, large solid shapes, thick necessary bridges, large open negative spaces, minimal interior lines, and no fine grille slats, spokes, trim, badges, shading lines, or other micro-detail.';
      if (tier === 'moderate') return 'Moderate detail: clean readable silhouettes, limited broad interior lines, practical bridge widths, and no dense parallel lines, fragile trim, tiny gaps, or micro-detail.';
      return 'Moderately detailed while remaining plasma-cuttable: use simplified intentional linework, strong necessary bridges, readable negative space, and durable connected metal; never use dense grille, spoke, texture, or shading lines.';
    }

    syncBackerRequirement() {
      const backer = this.form.elements['contact[Backer colour]'];
      const needsBacker = this.value('contact[Construction]') === 'two-layer';
      const note = this.querySelector('[data-backer-note]');
      backer.required = needsBacker;
      backer.setCustomValidity(needsBacker && !backer.value ? 'Please select a contrasting backer colour.' : '');
      note.hidden = !needsBacker;
    }

    syncFinishWithMaterial() {
      const material = this.value('contact[Material]');
      const finish = this.form.elements['contact[Finish]'];
      const note = this.querySelector('[data-aluminum-finish-note]');
      const clearOption = finish.querySelector('option[value="clear-powder"]');
      const isAluminum = material === 'brushed-aluminum-clear';

      if (isAluminum) {
        if (finish.value !== 'clear-powder') finish.dataset.previousFinish = finish.value;
        [...finish.options].forEach((option) => {
          const isClear = option.value === 'clear-powder';
          option.hidden = !isClear;
          option.disabled = !isClear;
        });
        clearOption.hidden = false;
        clearOption.disabled = false;
        finish.value = 'clear-powder';
      } else {
        [...finish.options].forEach((option) => {
          const isClear = option.value === 'clear-powder';
          option.hidden = isClear;
          option.disabled = isClear;
        });
        if (finish.value === 'clear-powder') finish.value = finish.dataset.previousFinish || 'black';
      }

      note.hidden = !isAluminum;
    }

    updateEstimate() {
      const width = Math.max(1, Number(this.value('contact[Width]')) || 24);
      const height = Math.max(1, Number(this.value('contact[Height]')) || 12);
      const area = width * height / 144;
      const material = this.value('contact[Material]');
      const layers = this.value('contact[Construction]');
      const base = Number(this.dataset.basePrice) || 75;
      const squareFoot = Number(this.dataset.squareFootPrice) || 32;
      const materialFactor = material === 'brushed-aluminum-clear' ? 1.35 : material === '11ga-steel' ? 1.22 : 1;
      const complexityAdd = 40;
      const constructionAdd = layers === 'two-layer' ? 120 : 0;
      const center = Math.max(Number(this.dataset.minimumPrice) || 95, (base + area * squareFoot) * materialFactor + complexityAdd + constructionAdd);
      const priceIncrease = 1.10;
      const low = Math.round(center * .9 * priceIncrease / 5) * 5;
      this.querySelector('[data-estimate]').textContent = this.currency.format(low);
      this.estimateText = `${this.currency.format(low)} CAD`;
    }

    buildSummary() {
      const shape = this.value('contact[Shape]');
      const sizeText = shape === 'round'
        ? `${this.value('contact[Width]') || '—'} in diameter`
        : shape === 'square'
          ? `${this.value('contact[Width]') || '—'} in square`
          : `${this.value('contact[Width]') || '—'} × ${this.value('contact[Height]') || '—'} in`;
      const rows = [
        ['Size', sizeText],
        ['Shape', this.value('contact[Shape]') || '—'],
        ['Material', this.label('contact[Material]') || '—'],
        ['Finish', this.label('contact[Finish]') || '—'],
        ['Backer colour', this.label('contact[Backer colour]') || 'none'],
        ['Construction', this.label('contact[Construction]') || '—'],
        ['Design detail', `${this.detailTierLabel()} (optimized for size)`],
        ['Sign price', this.estimateText || '—']
      ];
      this.querySelector('[data-summary]').innerHTML = rows.map(([label, value]) => `<div class="rhen-builder__summary-row"><span>${this.escape(label)}</span><strong>${this.escape(value)}</strong></div>`).join('');
      const description = this.value('contact[Design description]');
      this.form.elements['contact[body]'].value = [
        'RHENWORKS CUSTOM BUILDER REQUEST', '', ...rows.map(([a,b]) => `${a}: ${b}`),
        `Sign text: ${this.value('contact[Sign text]')}`,
        `Detail level: ${this.detailTierLabel()} — ${this.detailGuidance()}`,
        `Backer colour: ${this.label('contact[Backer colour]') || 'None'}`,
        `Use: ${this.value('contact[Use]')}`,
        `Reference link: ${this.value('contact[Reference link]') || 'None'}`, '',
        `AI preview: ${this.previewUrl || 'Not generated'}`, '',
        'Customer description:', description
      ].join('\n');
    }

    async generate() {
      const button = this.querySelector('[data-generate]');
      const message = this.querySelector('[data-ai-message]');
      this.refresh();
      if (!this.endpoint) {
        message.textContent = 'Your live concept has been updated. RHENWorks will turn this direction into a detailed, cut-ready proof after reviewing your request.';
        message.hidden = false;
        return;
      }
      button.disabled = true;
      button.textContent = 'Generating…';
      message.hidden = true;
      try {
        const response = await fetch(this.endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
          width: this.value('contact[Width]'), height: this.value('contact[Height]'), shape: this.value('contact[Shape]'), material: this.label('contact[Material]'), finish: this.label('contact[Finish]'), backer: this.label('contact[Backer colour]'), construction: this.label('contact[Construction]'), use: this.label('contact[Use]'), detailLevel: this.detailTier(), signText: this.value('contact[Sign text]'), description: `MANDATORY OUTPUT REQUIREMENT: Create production artwork only, shown perfectly straight-on. Use pure solid black and pure solid white only, with no grey, colour, gradients, shadows, lighting, texture, wall, room, scenery, mounting hardware, perspective, depth, bevels, or photographic mockup. Fill the entire image with one clean, high-resolution sign design. LETTERING RULE: Use normal clean lettering, never a stencil font. Do not add bridges, tabs, bars, or cuts through letters that do not need them. Only add the minimum discreet bridge needed to retain a truly enclosed counter in letters such as A, B, D, O, P, Q, or R; open letters must remain completely unbridged. PLASMA RULE: Make retained design elements physically connected, but do not over-bridge. Avoid floating islands, fragile lines, tiny gaps, dense parallel lines, and excessive detail. Vehicles and machinery must use bold simplified silhouettes with solid major forms, simple wheels, and only a few broad defining openings—no detailed grille slats, spokes, trim, badges, reflections, or mechanical linework. The result must be suitable for tracing into CNC plasma cut-file software. MANDATORY SIZE-BASED FABRICATION RULE: ${this.detailGuidance()} Customer request: ${this.value('contact[Design description]')}`
        }) });
        if (!response.ok) throw new Error('Preview service unavailable');
        const data = await response.json();
        if (!data.image_url) throw new Error('No preview returned');
        const image = this.querySelector('[data-ai-image]');
        image.src = data.image_url;
        image.classList.add('is-visible');
        this.previewUrl = data.preview_url || data.image_url;
        this.form.elements['contact[AI preview URL]'].value = this.previewUrl;
        this.generatedSignature = this.designSignature();
        message.textContent = 'Preview saved. Its private link will be included with your order request.';
        message.hidden = false;
        this.refresh();
      } catch (error) {
        message.textContent = 'The AI preview is temporarily unavailable, but you can still submit your complete request for a RHENWorks concept.';
        message.hidden = false;
      } finally {
        button.disabled = false;
        const previewIsCurrent = this.generatedSignature === this.designSignature();
        button.textContent = previewIsCurrent ? 'Regenerate preview' : 'See my concept preview';
        button.classList.toggle('rhen-builder__button--secondary', previewIsCurrent);
        button.classList.toggle('rhen-builder__button--preview', !previewIsCurrent);
      }
    }

    escape(value) {
      const div = document.createElement('div'); div.textContent = value; return div.innerHTML;
    }
  });
}
